/**
 * A imagem do passaporte, desenhada no aparelho (canvas): título, contagem
 * e os carimbos em grade. Sem servidor, sem biblioteca — é para mandar no
 * WhatsApp.
 */
import type { Carimbo } from './api';

const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

function quebrar(ctx: CanvasRenderingContext2D, texto: string, largura: number, maxLinhas: number): string[] {
  const palavras = texto.split(/\s+/);
  const linhas: string[] = [];
  let atual = '';
  for (const p of palavras) {
    const tentativa = atual ? `${atual} ${p}` : p;
    if (ctx.measureText(tentativa).width <= largura || !atual) atual = tentativa;
    else {
      linhas.push(atual);
      atual = p;
    }
  }
  if (atual) linhas.push(atual);
  if (linhas.length > maxLinhas) {
    const corte = linhas.slice(0, maxLinhas);
    corte[maxLinhas - 1] = `${corte[maxLinhas - 1]!.slice(0, 14)}…`;
    return corte;
  }
  return linhas;
}

export async function imagemDoPassaporte(params: { nome: string; viagem: string; carimbos: Carimbo[]; lugares: number; dias: number }): Promise<Blob> {
  const W = 1080;
  const colunas = 3;
  const cel = 300;
  const margem = 60;
  const topo = 300;
  const linhas = Math.max(1, Math.ceil(params.carimbos.length / colunas));
  const H = topo + linhas * cel + 120;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Sem canvas 2D neste navegador.');

  ctx.fillStyle = '#F7F5F2';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#141414';
  ctx.font = '800 64px system-ui, sans-serif';
  ctx.fillText('Meu passaporte', margem, 120);
  ctx.font = '600 34px system-ui, sans-serif';
  ctx.fillStyle = '#6B6560';
  ctx.fillText(`${params.nome} · ${params.viagem}`, margem, 180);
  ctx.fillStyle = '#F8481D';
  ctx.font = '800 40px system-ui, sans-serif';
  ctx.fillText(`${params.lugares} ${params.lugares === 1 ? 'lugar' : 'lugares'} em ${params.dias} ${params.dias === 1 ? 'dia' : 'dias'}`, margem, 240);

  params.carimbos.forEach((c, i) => {
    const col = i % colunas;
    const lin = Math.floor(i / colunas);
    const cx = margem + col * ((W - 2 * margem) / colunas) + (W - 2 * margem) / colunas / 2;
    const cy = topo + lin * cel + cel / 2;
    const r = 120;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(((i % 5) - 2) * 0.06);
    ctx.strokeStyle = '#9B7BF7';
    ctx.lineWidth = 6;
    ctx.setLineDash([14, 10]);
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(0, 0, r - 16, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = '#5B3FB8';
    ctx.textAlign = 'center';
    ctx.font = '800 25px system-ui, sans-serif';
    const ls = quebrar(ctx, c.place.toUpperCase(), r * 1.55, 3);
    ls.forEach((l, k) => ctx.fillText(l, 0, -4 + k * 30 - (ls.length - 1) * 15));
    ctx.font = '600 24px ui-monospace, monospace';
    ctx.fillText(ddmm(c.date), 0, 72);
    ctx.restore();
  });

  ctx.fillStyle = '#6B6560';
  ctx.font = '600 26px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('Viajandos', W - margem, H - 50);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Não foi possível gerar a imagem.'))), 'image/png');
  });
}
